import { useState } from 'react'
import { Modal } from '@/components/ui/modal'
import { useGrantCredits } from '@/hooks/use-accounts'
import { useAuth } from '@/lib/auth-context'
import { formatNumber } from '@/lib/format'
import type { AccountDetailResponse } from '@/lib/types'

interface BillingTabProps {
  account: AccountDetailResponse
}

export function BillingTab({ account }: BillingTabProps): React.JSX.Element {
  const { isViewer } = useAuth()
  const grantCredits = useGrantCredits()

  const [showGrantModal, setShowGrantModal] = useState(false)
  const [creditAmount, setCreditAmount] = useState('')
  const [creditReason, setCreditReason] = useState('')

  const effectiveLimit = account.overrides?.email_limit ?? getDefaultLimit(account.tier)
  const percentUsed =
    effectiveLimit > 0 ? Math.min((account.emails_sent_this_period / effectiveLimit) * 100, 100) : 0

  function handleGrantCredits(): void {
    const amount = Number(creditAmount)
    if (amount <= 0 || !creditReason.trim()) return
    grantCredits.mutate(
      { id: account.id, amount, reason: creditReason },
      {
        onSuccess: () => {
          setShowGrantModal(false)
          setCreditAmount('')
          setCreditReason('')
        },
      },
    )
  }

  return (
    <div className="space-y-6">
      {/* Usage bar */}
      <div className="rounded-xl border border-surface-3 bg-surface-0 p-6">
        <h3 className="mb-4 text-sm font-medium text-text-primary">Current Period Usage</h3>
        {account.period_start && account.period_end && (
          <p className="mb-3 text-xs text-text-tertiary">
            {new Date(account.period_start).toLocaleDateString()} -{' '}
            {new Date(account.period_end).toLocaleDateString()}
          </p>
        )}
        <div className="mb-2 flex items-end justify-between">
          <span className="text-2xl font-semibold text-text-primary">
            {formatNumber(account.emails_sent_this_period)}
          </span>
          <span className="text-sm text-text-secondary">
            / {formatNumber(effectiveLimit)}
            {account.overrides?.email_limit ? ' (override)' : ''}
          </span>
        </div>
        <div className="h-3 w-full overflow-hidden rounded-full bg-surface-2">
          <div
            className={`h-full rounded-full transition-all ${percentUsed >= 100 ? 'bg-red-500' : percentUsed >= 80 ? 'bg-yellow-500' : 'bg-admin-500'}`}
            style={{ width: `${Math.min(percentUsed, 100)}%` }}
          />
        </div>
        <p className="mt-1 text-xs text-text-tertiary">{percentUsed.toFixed(1)}% used</p>
      </div>

      {/* Credits & overage info */}
      <div className="grid gap-4 sm:grid-cols-3">
        <InfoCard
          label="Email Credits"
          value={formatNumber(account.email_credits)}
          sub="Extra credits granted"
        />
        <InfoCard
          label="Overage Count"
          value={String(account.overage_count)}
          sub={overageStatusText(account.overage_count)}
        />
        <InfoCard
          label="Overage Policy"
          value={account.overrides?.overage_policy ?? 'Tier default'}
          sub={account.overrides?.overage_policy ? 'Custom override' : 'Based on tier'}
        />
      </div>

      {/* Actions */}
      {!isViewer && (
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setShowGrantModal(true)}
            className="rounded-lg bg-admin-600 px-4 py-2 text-sm font-medium text-white hover:bg-admin-700"
          >
            Grant Credits
          </button>
        </div>
      )}

      {/* Grant credits modal */}
      <Modal
        open={showGrantModal}
        onClose={() => setShowGrantModal(false)}
        title="Grant Email Credits"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-secondary">
            Add extra email credits for{' '}
            <span className="font-medium text-text-primary">{account.user_email}</span> for the
            current period.
          </p>
          <div>
            <label htmlFor="credit-amount" className="block text-sm font-medium text-text-primary">
              Amount
            </label>
            <input
              id="credit-amount"
              type="number"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              placeholder="e.g., 5000"
              className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
            />
          </div>
          <div>
            <label htmlFor="credit-reason" className="block text-sm font-medium text-text-primary">
              Reason
            </label>
            <textarea
              id="credit-reason"
              value={creditReason}
              onChange={(e) => setCreditReason(e.target.value)}
              placeholder="Why are credits being granted?"
              className="mt-1 w-full rounded-lg border border-surface-3 bg-surface-0 p-2 text-sm text-text-primary placeholder:text-text-tertiary focus:border-admin-500 focus:outline-none"
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowGrantModal(false)}
              className="rounded-lg border border-surface-3 px-4 py-2 text-sm text-text-secondary hover:bg-surface-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleGrantCredits}
              disabled={
                !creditAmount ||
                Number(creditAmount) <= 0 ||
                !creditReason.trim() ||
                grantCredits.isPending
              }
              className="rounded-lg bg-admin-600 px-4 py-2 text-sm font-medium text-white hover:bg-admin-700 disabled:opacity-50"
            >
              {grantCredits.isPending ? 'Granting...' : 'Grant Credits'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function InfoCard({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub: string
}): React.JSX.Element {
  return (
    <div className="rounded-xl border border-surface-3 bg-surface-0 p-4">
      <p className="text-xs text-text-tertiary">{label}</p>
      <p className="mt-1 text-xl font-semibold text-text-primary">{value}</p>
      <p className="mt-0.5 text-xs text-text-tertiary">{sub}</p>
    </div>
  )
}

function getDefaultLimit(tier: string): number {
  const limits: Record<string, number> = {
    free: 10000,
    starter: 25000,
    pro: 75000,
    growth: 300000,
    scale: 2000000,
  }
  return limits[tier] ?? 10000
}

function overageStatusText(count: number): string {
  if (count === 0) return 'No overages'
  if (count <= 3) return `Overage #${count} (friendly warning)`
  return `Overage #${count} (hard-block eligible)`
}
